/* Обёртка над Telegram WebApp SDK.
   В обычном браузере всё вырождается в no-op, чтобы приложение работало и вне Telegram. */

import { applyTheme, watchEnvTheme } from "./theme.js";

export const tg = typeof window !== "undefined" ? window.Telegram?.WebApp : undefined;
export const inTelegram = !!tg?.initData;

/** Развернуть на весь экран, применить тему, подписаться на её смену */
export function initTelegram() {
  if (inTelegram) {
    tg.ready();
    tg.expand?.();
    tg.disableVerticalSwipes?.();
    goFullscreen();
    watchSafeAreas();
  }
  applyTheme();
  watchEnvTheme();
}

/** Полноэкранный режим (Bot API 8.0); на старых клиентах остаётся expand() */
function goFullscreen() {
  if (typeof tg.requestFullscreen !== "function") return;
  tg.onEvent?.("fullscreenChanged", syncSafeAreas);
  tg.onEvent?.("fullscreenFailed", (e) => {
    // UNSUPPORTED / ALREADY_FULLSCREEN — не ошибка, просто остаёмся в обычном режиме
    console.info("Полноэкранный режим недоступен:", e?.error || e);
  });
  try {
    if (!tg.isFullscreen) tg.requestFullscreen();
  } catch {
    /* старый клиент — игнорируем */
  }
}

/* В фуллскрине контент уходит под шапку Telegram с кнопками «⋮» и «✕»,
   поэтому отступы берём из его безопасных зон, а не из env(). */
function syncSafeAreas() {
  const root = document.documentElement.style;
  const safe = tg.safeAreaInset || {};
  const content = tg.contentSafeAreaInset || {};
  const px = (v) => `${Math.max(0, Number(v) || 0)}px`;

  // Верх: системная зона (чёлка) + зона под шапкой Telegram
  root.setProperty("--tg-top", px((safe.top || 0) + (content.top || 0)));
  root.setProperty("--tg-bottom", px((safe.bottom || 0) + (content.bottom || 0)));
  root.setProperty("--tg-left", px(safe.left));
  root.setProperty("--tg-right", px(safe.right));
  document.documentElement.classList.toggle("tg-fullscreen", !!tg.isFullscreen);
}

function watchSafeAreas() {
  syncSafeAreas();
  tg.onEvent?.("safeAreaChanged", syncSafeAreas);
  tg.onEvent?.("contentSafeAreaChanged", syncSafeAreas);
  tg.onEvent?.("viewportChanged", syncSafeAreas);
}

/* ── Кнопка «Назад» в шапке Telegram ── */
export function showBackButton(handler) {
  if (!tg?.BackButton) return () => {};
  tg.BackButton.show();
  tg.BackButton.onClick(handler);
  return () => {
    tg.BackButton.offClick(handler);
    tg.BackButton.hide();
  };
}

/* ── Главная кнопка (сохранить / подтвердить) ── */
export function mainButton({ text, onClick, color, textColor }) {
  const mb = tg?.MainButton;
  if (!mb) return () => {};
  mb.setParams({
    text,
    color: color || tg.themeParams?.button_color,
    text_color: textColor || tg.themeParams?.button_text_color,
    is_active: true,
    is_visible: true,
  });
  mb.onClick(onClick);
  return () => {
    mb.offClick(onClick);
    mb.hide();
  };
}

export const setMainButtonProgress = (on) => {
  if (!tg?.MainButton) return;
  if (on) tg.MainButton.showProgress(false);
  else tg.MainButton.hideProgress();
};

/* ── Тактильная отдача ── */
export const haptic = {
  light: () => tg?.HapticFeedback?.impactOccurred?.("light"),
  medium: () => tg?.HapticFeedback?.impactOccurred?.("medium"),
  select: () => tg?.HapticFeedback?.selectionChanged?.(),
  success: () => tg?.HapticFeedback?.notificationOccurred?.("success"),
  warning: () => tg?.HapticFeedback?.notificationOccurred?.("warning"),
  error: () => tg?.HapticFeedback?.notificationOccurred?.("error"),
};

/* ── Диалоги ── */
export function confirmDialog(message) {
  return new Promise((resolve) => {
    if (tg?.showConfirm) tg.showConfirm(message, resolve);
    else resolve(window.confirm(message));
  });
}

export function alertDialog(message) {
  if (tg?.showAlert) tg.showAlert(message);
  else window.alert(message);
}

/** Внешняя ссылка: в Telegram открывается системным браузером, вне — новой вкладкой */
export function openLink(url) {
  /* Схемы приложений (shortcuts://) Telegram через openLink не пропускает — он ждёт http(s).
     Обычный переход по адресу WebView отдаёт системе, и она открывает нужное приложение. */
  if (!/^https?:/i.test(url)) {
    window.location.href = url;
    return;
  }
  if (tg?.openLink) tg.openLink(url);
  else window.open(url, "_blank", "noopener");
}

/* ── Скачивание файла на устройство (Bot API 8.0) ──
   Telegram требует от ссылки заголовки Content-Disposition: attachment и
   Access-Control-Allow-Origin — без них загрузка срывается без объяснений.
   Поэтому годятся только наши собственные ручки, а не чужие CDN. */
export const canDownload = () => typeof tg?.downloadFile === "function";

/** Показывает системный запрос на скачивание; отвечает, согласился ли человек */
export function downloadFile(url, fileName) {
  return new Promise((resolve) => {
    if (!canDownload()) {
      // Вне Telegram — обычная ссылка, файл заберёт сам браузер
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "";
      document.body.appendChild(a);
      a.click();
      a.remove();
      resolve(true);
      return;
    }
    let done = false;
    const finish = (ok) => {
      if (done) return;
      done = true;
      resolve(!!ok);
    };
    try {
      tg.downloadFile({ url, file_name: fileName }, finish);
    } catch {
      finish(false);
    }
    // Старые сборки колбэк не зовут — не подвешиваем на этом весь экран
    setTimeout(() => finish(true), 30_000);
  });
}

/** Копирование в буфер. Старый WebView не знает clipboard API — тогда через скрытое поле */
export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand?.("copy");
    document.body.removeChild(el);
    return !!ok;
  }
}

/** Закрыть мини-апп: дальше человек продолжает в переписке с ботом */
export const closeApp = () => tg?.close?.();

export const initData = () => tg?.initData || "";
export const tgUser = () => tg?.initDataUnsafe?.user || null;
